import { useEffect, useMemo, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@verantos-dev/core-annotation-tool';

import { useGetAnnotatorsQuery } from '../../api/createProjectApiSlice';
import {
  useAnnotationAgentAccountId,
  useIsAnnotationAgentOn,
} from '../../hooks/useAnnotationAgentAllocation';
import {
  addSelectedAnnotator,
  AnnotationUser,
  removeSelectedAnnotator,
} from '../../state/createProjectSlice';
import { selectSelectedAnnotators } from '../../state/selector';
import { AnnotatorResponse } from '../../types/response';

interface UseAnnotatorsSelectionResults {
  annotators: AnnotatorResponse[];
  selectedAnnotators: AnnotationUser[];
  handleAddAnnotator: (annotator: AnnotatorResponse) => void;
  handleRemoveAnnotator: (annotator: AnnotatorResponse) => void;
  annotatorInputValue: string;
  lastTypedAnnotatorValue: React.MutableRefObject<string>;
  setAnnotatorInputValue: (value: string) => void;
  isAnnotatorsLoading: boolean;
  // TODO: cleanup with cp_annotation_createproject_agent ff
  /** The annotation agent is on, so it is shown as a fixed first chip. */
  isAgentOn: boolean;
}

export const useAnnotatorsSelection = (): UseAnnotatorsSelectionResults => {
  // TODO: cleanup with cp_annotation_createproject_agent ff
  // While the agent is on, agent accounts are left out of the options: the server assigns the
  // agent, so it cannot also be picked by hand.
  const isAgentOn = useIsAnnotationAgentOn();
  const agentId = useAnnotationAgentAccountId(isAgentOn);
  const {
    data: annotatorsData,
    isLoading: isAnnotatorsLoading,
    isFetching: isAnnotatorsFetching,
  } = useGetAnnotatorsQuery({ includeNonAssignable: !isAgentOn });
  // While the list with agents is refetched without them, the previous data is still returned.
  const annotators = useMemo(
    () => (annotatorsData?.data ?? []).filter((annotator) => annotator.id !== agentId),
    [annotatorsData, agentId]
  );

  const dispatch = useAppDispatch();
  const selectedAnnotators = useAppSelector(selectSelectedAnnotators);

  // TODO: cleanup with cp_annotation_createproject_agent ff
  // An agent picked by hand while the agent was off is dropped once it is turned on.
  useEffect(() => {
    if (agentId && selectedAnnotators.some((annotator) => annotator.id === agentId)) {
      dispatch(removeSelectedAnnotator(agentId));
    }
  }, [agentId, selectedAnnotators, dispatch]);

  const handleAddAnnotator = (annotator: AnnotatorResponse) => {
    dispatch(addSelectedAnnotator(annotator));
  };
  const handleRemoveAnnotator = (annotator: AnnotatorResponse) => {
    dispatch(removeSelectedAnnotator(annotator.id));
  };
  const [annotatorInputValue, setAnnotatorInputValue] = useState<string>('');

  const lastTypedAnnotatorValue = useRef<string>('');
  return {
    selectedAnnotators,
    handleRemoveAnnotator,
    annotators,
    annotatorInputValue,
    lastTypedAnnotatorValue,
    setAnnotatorInputValue,
    handleAddAnnotator,
    isAnnotatorsLoading: isAnnotatorsLoading || isAnnotatorsFetching,
    isAgentOn,
  };
};
